import React, { PureComponent, createRef, useRef, useImperativeHandle } from 'react'

import MaterialTable from 'material-table'
import PropTypes from 'prop-types'

import { reduce } from 'lodash'
import filter from 'lodash/filter'
import flow from 'lodash/fp/flow'
import get from 'lodash/get'
import isBoolean from 'lodash/isBoolean'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isFunction from 'lodash/isFunction'
import map from 'lodash/map'
import omitBy from 'lodash/omitBy'
import snakeCase from 'lodash/snakeCase'

import { ButtonGroup } from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import { ThemeProvider, withStyles } from '@material-ui/core/styles'
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons'

import { withT } from '@galo-monitor/i18n'
import colors from '@galo-monitor/styles/colors'
import fonts from '@galo-monitor/styles/fonts'
import Button from '@galo-monitor/web-components/Button'
import Popover from '@galo-monitor/web-components/Popover'

import Header from './components/Header'
import HeaderToolbar from './components/HeaderToolbar'
import datagridI18 from './i18n'
import icons from './icons'
import options from './options'
import styles from './styles'


class DataTable extends PureComponent {
  configs = undefined

  constructor(props) {
    super(props)

    // eslint-disable-next-line react/prop-types
    this.i18n = datagridI18(props.t)

    this.multipleActionsRef = createRef()

    this.handleClickMultipleAction = this.handleClickMultipleAction.bind(this)

    this.state = {
      filteredRows: [],
      softDeleteChecked: false,
      multipleActionsAnchorOpen: false
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      urlParams,
      queryParams,
      tableRef,
      data,
      onSoftDeleteChange,
      filters
    } = this.props

    const {
      softDeleteChecked
    } = this.state

    if (tableRef && isFunction(data)) {
      if (
        !isEqual(prevProps.data, data)
        || !isEqual(prevProps.urlParams, urlParams)
        || !isEqual(prevProps.queryParams, queryParams)
        || !isEqual(prevState.softDeleteChecked, softDeleteChecked)
        || !isEqual(prevProps.filters, filters)
      ) {
        tableRef.current.onQueryChange()
      }
    }

    if (!isEqual(prevState.softDeleteChecked, softDeleteChecked)) {
      onSoftDeleteChange(softDeleteChecked)
    }
  }

  handleSoftDeleteCheck = () => {
    this.setState(old => ({
      ...old,
      softDeleteChecked: !old.softDeleteChecked
    }))
  }

  handleClickMultipleAction = (onClick) => {
    onClick()
    this.multipleActionsRef && this.multipleActionsRef.current && this.multipleActionsRef.current.onClose()
  }

  asyncData = async (query) => {
    const {
      data,
      pageSize,
      params,
      urlParams,
      queryParams,
      filters: customFilters
    } = this.props

    const { softDeleteChecked } = this.state

    try {
      const searchTerm = get(query, 'search')
      const size = get(query, 'pageSize', pageSize)
      const orderBy = get(query, 'orderBy.field')
      const orderDirection = get(query, 'orderDirection')
      let page = get(query, 'page', 0)

      const filters = reduce(customFilters, (acc, { name, value }) => ({ ...acc, [name]: value }), {})

      const currentConfigs = {
        size,
        searchTerm,
        orderBy,
        orderDirection,
        ...filters
      }

      if (!isEqual(currentConfigs, this.configs)) {
        page = 0 // if has changes, return to first page
      }

      this.configs = { ...currentConfigs }

      let paginationConfig = {
        page: page + 1,
        limit: size,
        softDelete: softDeleteChecked
      }

      if (!isEmpty(orderBy)) {
        paginationConfig = {
          ...paginationConfig,
          orderBy: `${ orderDirection === 'desc' ? '-' : '' }${ snakeCase(orderBy) }`
        }
      }
      if (!isEmpty(searchTerm)) {
        paginationConfig = {
          ...paginationConfig,
          q: searchTerm
        }
      }
      if (!isEmpty(queryParams)) {
        paginationConfig = {
          ...paginationConfig,
          ...omitBy(queryParams, param => !isBoolean(param) && param !== 0 && isEmpty(param))
        }
      }

      const {
        data: {
          data: datagridData,
          meta: {
            total: totalRecords
          }
        }
      } = await data({
        ...params,
        ...paginationConfig,
        ...filters
      }, urlParams)

      return {
        data: datagridData,
        page,
        totalCount: totalRecords || 0
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(this.i18n.fetchLoadError, e)
      throw e
    }
  }

  render() {
    const {
      // eslint-disable-next-line react/prop-types
      t,
      classes,
      data,
      options: propOptions,
      noShadow,
      title,
      CustomAddButton,
      onAddClick,
      onMultipleActionsPopover,
      addTitle,
      addTooltip,
      onEditClick,
      onDeleteClick,
      onExportClick,
      onRowClick,
      pageSize,
      loading,
      disabled,
      disabledRow,
      onSelectionChange,
      tableBodyClassName,
      tableHeaderClassName,
      sortLabelClasses,
      components,
      emptyMessage,
      actions: customActions,
      filters,
      customActionsAtLast,
      softDelete,
      ...otherProps
    } = this.props

    let { columns } = this.props

    const { /* filteredRows, */ softDeleteChecked } = this.state

    const AddButton = CustomAddButton || (onAddClick && (
      <ButtonGroup variant="contained" ref={ this.multipleActionsRef }>
        <Button
          id="new"
          color="primary"
          onClick={ onAddClick }
          disabled={ loading || disabled }
        >
          {addTitle || t('register')}
        </Button>
        {!isEmpty(onMultipleActionsPopover) ? (
          <Popover
            ref={ this.multipleActionsRef }
            popoverId="multiple-actions"
            Component={ props => (
              <Button { ...props }>
                <ArrowDropDownIcon />
              </Button>
            ) }
            ComponentProps={ {
              id: 'open-multiple-actions-popover',
              size: 'small',
              disabled: loading || disabled,
              style: {
                padding: 0,
                minWidth: 35,
                height: '100%',
                borderRadius: '0 4px 4px 0',
                boxShadow: 'none'
              }
            } }
            anchorOrigin={ {
              vertical: 50,
              horizontal: -75
            } }
          >
            <List style={ { padding: 0 } }>
              {map(onMultipleActionsPopover, ({ text, onClick }) => (
                <React.Fragment key={ text }>
                  <ListItem button onClick={ () => this.handleClickMultipleAction(onClick) }>
                    <ListItemText className={ classes.popoverItem } primary={ text } />
                  </ListItem>
                  <Divider style={ { margin: 0 } } />
                </React.Fragment>
              ))}
            </List>
          </Popover>
        ) : null}
      </ButtonGroup>
    ))

    let newActions = []

    if (onExportClick) {
      const ExportIcon = icons.Export
      newActions = [
        {
          icon: ExportIcon,
          tooltip: t('datagrid-toolbar-export-title'),
          onClick: onExportClick,
          isFreeAction: true,
          position: 'toolbar',
          disabled: loading || disabled
        },
        ...newActions
      ]
    }

    if (onRowClick) {
      const AccessRowIcon = icons.AccessRow
      newActions = [
        {
          icon: AccessRowIcon,
          tooltip: t('datagrid body access tooltip'),
          onClick: onRowClick,
          disabled: loading || disabled,
          iconButtonProps: {
            size: 'small'
          },
          position: 'row'
        },
        ...newActions
      ]
    }

    if (onDeleteClick) {
      const DeleteIcon = icons.Delete
      newActions = [
        {
          icon: DeleteIcon,
          tooltip: t('datagrid body delete tooltip'),
          onClick: onDeleteClick,
          disabled: loading || disabled,
          iconButtonProps: {
            size: 'small'
          },
          position : 'row'
        },
        ...newActions
      ]
    }

    if (onEditClick) {
      const EditIcon = icons.Edit
      newActions = [
        {
          icon: EditIcon,
          tooltip: t('datagrid body edit tooltip'),
          onClick: onEditClick,
          disabled: loading || disabled,
          iconButtonProps: {
            size: 'small'
          },
          position: 'row'
        },
        ...newActions
      ]
    }

    const actions = customActionsAtLast
      ? [...newActions, ...customActions]
      : [...customActions, ...newActions]
    // TODO: check what toolbarOnSelect action position does
    // map(
    //   [...customActions, ...newActions],
    //   (action) => ({
    //     ...action,
    //     onClick: action.position === 'toolbarOnSelect'
    //       ? () => action.onClick(filteredRows)
    //       : action.onClick
    //   })
    // )

    columns = map(columns, (column) => {
      const { cellStyle = {} } = column

      let columnStyle = cellStyle
      if (!isFunction(cellStyle)) {
        columnStyle = () => cellStyle
      }

      return {
        ...column,
        emptyValue: column.render ? undefined : '-',
        cellStyle: (_, rowData) => {
          let rowStyle = {}
          if (disabledRow(rowData)) {
            rowStyle = {
              opacity: 0.5
            }
          }
          return {
            ...columnStyle(_, rowData),
            ...rowStyle
          }
        }
      }
    })

    const customOptions = {
      selectionProps: (row) => {
        const rowDisabled = disabledRow(row)
        const response = {
          // color: 'primary',
          disabled: rowDisabled
        }
        if (rowDisabled) {
          response.checked = false
        }
        return response
      },
      ...options,
      ...propOptions,
      showTitle: !isEmpty(title),
      pageSize,
      searchFieldProps: {
        ...options.searchFieldProps,
        ...get(propOptions, 'searchFieldProps', {}),
        classes: {
          root: classes.searchField,
          ...get(options, 'searchFieldProps.classes', {}),
          ...get(propOptions, 'searchFieldProps.classes', {})
        },
        InputProps: {
          endAdornment: null
        }
      }
      // tableBodyProps: {
      //   className: classes.stripedRows
      // }
    }

    let style = {}
    if (noShadow) {
      style = {
        ...style,
        boxShadow: 'none'
      }
    }

    let realData = data
    let customProps = {}
    if (isFunction(data)) {
      realData = this.asyncData
    } else {
      customProps = {
        ...customProps,
        isLoading: loading
      }
    }

    const i18n = {
      ...(this.i18n || {}),
      body: {
        ...get(this.i18n, 'body', {}),
        emptyDataSourceMessage: emptyMessage || get(this.i18n, 'body.emptyDataSourceMessage')
      }
    }

    return (
      <ThemeProvider
        theme={ (theme) => ({
          ...theme,
          overrides: {
            ...theme.overrides,
            MuiTableCell: {
              ...(theme.overrides.MuiTableCell || {}),
              root: {
                borderBottomColor: `${ colors.lightGrey } !important`,
                fontSize: fonts.fontSize.S
              },
              body: {
                fontSize: fonts.fontSize.S
              },
              head: {
                fontWeight: fonts.fontWeight.bold
              }
            },
            MuiTablePagination: {
              ...(theme.overrides.MuiTablePagination || {}),
              root: {
                padding: '0 5px !important'
              }
            },
            MuiPaper: {
              ...(theme.overrides.MuiPaper || {}),
              rounded: {
                borderRadius: 4
              }
            },
            MuiCheckbox: {
              ...(theme.overrides.MuiCheckbox || {}),
              root: {
                marginLeft: '5px !important'
              }
            },
            MuiTypography: {
              ...(theme.overrides.MuiTypography || {}),
              caption: {
                padding: '0 5px'
              }
            }
          }
        }) }
      >
        <MaterialTable
          { ...otherProps }
          { ...customProps }
          icons={ icons }
          options={ customOptions }
          columns={ columns }
          data={ realData }
          localization={ i18n }
          onRowClick={ onRowClick }
          style={ style }
          title={ title }
          actions={ actions }
          components={ {
            Header: (headerProps) => (
              <Header
                { ...headerProps }
                tableHeaderClassName={ tableHeaderClassName }
                sortLabelClasses={ sortLabelClasses }
              />
            ),
            Toolbar: (toolbarProps) => (
              <HeaderToolbar
                { ...toolbarProps }
                searchFieldAlignment="left"
                softDeleteAlignment="left"
                showTitle={ false }
                AddButton={ AddButton }
                softDelete={ softDelete }
                softDeleteChecked={ softDeleteChecked }
                handleSoftDeleteCheck={ this.handleSoftDeleteCheck }
                filters={ filters }
              />
            ),
            ...components
          } }
          onSelectionChange={ (rows) => {
            const newFilteredRows = filter(rows, (row) => !disabledRow(row))
            this.onSelectionChange(newFilteredRows)
            this.setState({ filteredRows: newFilteredRows })
          } }
        />
      </ThemeProvider>
    )
  }
}

DataTable.propTypes = {
  /** - */
  classes: PropTypes.object.isRequired,
  /** injected by @galo-monitor/i18n */
  t: PropTypes.func.isRequired,
  /** columns by material-table */
  columns: PropTypes.array.isRequired,
  /** actions by material-table */
  actions: PropTypes.array,
  /** custom filters */
  filters: PropTypes.array,
  customActionsAtLast: PropTypes.bool,
  /** data by material-table */
  data: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.func
  ]),
  /** options by material-table */
  options: PropTypes.object,
  /** params to inject into request */
  params: PropTypes.object,
  /** pageSize by material-table */
  pageSize: PropTypes.number,
  /** remove shadow of the datagrid container */
  noShadow: PropTypes.bool,
  /** title by material-table */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  CustomAddButton: PropTypes.element,
  /* Enable Add button, and set the callback called when this button is clicked */
  onAddClick: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ]),
  onMultipleActionsPopover: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    onClick: PropTypes.func
  })),
  /** tooltip for add button */
  addTooltip: PropTypes.string,
  /** title for add button */
  addTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onSelectionChange: PropTypes.func,
  /** Enable Edit button, and set the callback called when this button is clicked */
  onEditClick: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ]),
  /** Enable Delete button, and set the callback called when this button is clicked */
  onDeleteClick: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool
  ]),
  /** Enable Export button, and set the callback called when this button is clicked */
  onExportClick: PropTypes.func,
  /** Enable Row click */
  onRowClick: PropTypes.func,
  /** datagrid is loading */
  loading: PropTypes.bool,
  /** disable all actions */
  disabled: PropTypes.bool,
  /** customize row when row was softdeleted */
  disabledRow: PropTypes.func,
  /** customize row when row was softdeleted */
  emptyMessage: PropTypes.string,
  urlParams: PropTypes.object,
  queryParams: PropTypes.object,
  tableBodyClassName: PropTypes.string,
  tableHeaderClassName: PropTypes.string,
  sortLabelClasses: PropTypes.object,
  components: PropTypes.object,
  tableRef: PropTypes.any,
  softDelete: PropTypes.bool,
  onSoftDeleteChange: PropTypes.func
}

DataTable.defaultProps = {
  data: [],
  options: {},
  params: {},
  noShadow: false,
  actions: [],
  filters: [],
  customActionsAtLast: false,
  pageSize: 50,
  title: '',
  CustomAddButton: null,
  onAddClick: null,
  onMultipleActionsPopover: [],
  addTooltip: '',
  addTitle: null,
  onSelectionChange: () => [],
  onEditClick: null,
  onDeleteClick: null,
  onExportClick: null,
  onRowClick: undefined,
  loading: undefined,
  disabled: false,
  disabledRow: () => false,
  emptyMessage: null,
  urlParams: {},
  tableBodyClassName: null,
  tableHeaderClassName: null,
  sortLabelClasses: {},
  components: {},
  queryParams: {},
  tableRef: {},
  softDelete: undefined,
  onSoftDeleteChange: () => {}
}

export default flow(
  withStyles(styles),
  withT
)((props) => {
  const { tableRef, ...rest } = props

  const ref = useRef(null)

  useImperativeHandle(tableRef, () => ref.current)

  return <DataTable tableRef={ ref } { ...rest } />
})

export { DataTable as DataTableProps }
